import { supabase } from '../lib/supabase';

/* ======================================================
   APPOINTMENT SERVICE
====================================================== */

export const appointmentService = {

  /* --------------------------------------------------
     GET ALL APPOINTMENTS
  -------------------------------------------------- */
  async getAllAppointments() {
    const { data, error } = await supabase
      .from('appointments')
      .select(`
        *,
        bail_agents!appointments_bail_agent_id_fkey (
          id,
          full_name,
          email
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching appointments:', error);
      return { error };
    }

    return { data };
  },


  /* --------------------------------------------------
     CREATE APPOINTMENT
  -------------------------------------------------- */
  async createAppointment({
    clientName,
    clientEmail,
    clientPhone,
    bailAgentId,
    appointmentDate,
    purpose,
    purposeDetails
  }) {
    try {

      const confirmationNumber =
        'APT-' + Date.now().toString().slice(-6);

      const approvalToken =
        (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : Math.random().toString(36).substring(2) + Date.now().toString(36);

      const { data, error } = await supabase
        .from('appointments')
        .insert([{
          client_name: clientName,
          client_email: clientEmail,
          client_phone: clientPhone,
          bail_agent_id: bailAgentId,
          appointment_date: appointmentDate,
          purpose,
          purpose_details: purposeDetails,
          status: 'pending',
          approval_token: approvalToken,
          confirmation_number: confirmationNumber
        }])
        .select(`
          *,
          bail_agents!appointments_bail_agent_id_fkey (
            id,
            full_name,
            email
          )
        `)
        .single();

      if (error) throw error;

      return data;

    } catch (err) {
      console.error('Create appointment error:', err);
      throw err;
    }
  },


  /* --------------------------------------------------
     CHECK APPOINTMENT CONFLICT
  -------------------------------------------------- */
  async checkAppointmentConflict(bailAgentId, appointmentDateISO) {
    try {

      const appointmentDate = new Date(appointmentDateISO);
      const SLOT_DURATION_MINUTES = 30;

      const startOfDay = new Date(appointmentDate);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(appointmentDate);
      endOfDay.setHours(23, 59, 59, 999);

      const { data, error } = await supabase
        .from('appointments')
        .select('appointment_date')
        .eq('bail_agent_id', bailAgentId)
        .gte('appointment_date', startOfDay.toISOString())
        .lte('appointment_date', endOfDay.toISOString());

      if (error) {
        console.error('Conflict check error:', error);
        return false;
      }

      return data?.some(appt => {
        const existingTime = new Date(appt.appointment_date);
        const diff =
          Math.abs(existingTime - appointmentDate) / 60000;
        return diff < SLOT_DURATION_MINUTES;
      });

    } catch (err) {
      console.error('Appointment conflict exception:', err);
      return false;
    }
  },


  /* --------------------------------------------------
     SEND APPROVAL EMAIL
  -------------------------------------------------- */
  async sendApprovalEmail(
    bailAgentId,
    agentEmail,
    clientName,
    agentName,
    appointmentDateISO,
    purpose,
    purposeDetails,
    notes,
    confirmationNumber
  ) {
    try {

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            type: 'appointment_approval',
            to: agentEmail,
            data: {
              clientName,
              agentName,
              appointmentDate: appointmentDateISO,
              purpose,
              purposeDetails,
              notes,
              confirmationNumber
            }
          })
        }
      );

      if (!response.ok) {
        console.error('Failed to send approval email');
      }

      return true;

    } catch (err) {
      console.error('Send approval email error:', err);
      return false;
    }
  },


  /* --------------------------------------------------
     GET AVAILABLE TIME SLOTS
  -------------------------------------------------- */
  async getAvailableTimeSlots(bailAgentId, selectedDate) {
    try {

      const SLOT_DURATION_MINUTES = 30;
      const BUFFER_MINUTES = 45;

      const selectedDateObj = new Date(selectedDate);
      const dayOfWeek = selectedDateObj.getDay();

      const { data: scheduleData } = await supabase
        .from('agent_weekly_schedules')
        .select('*')
        .eq('bail_agent_id', bailAgentId)
        .eq('day_of_week', dayOfWeek)
        .single();

      if (!scheduleData || !scheduleData.is_available) {
        return [];
      }

      const [startHour, startMinute] =
        scheduleData.start_time.split(':').map(Number);

      const [endHour, endMinute] =
        scheduleData.end_time.split(':').map(Number);

      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);

      const { data: bookedAppointments } = await supabase
        .from('appointments')
        .select('appointment_date')
        .eq('bail_agent_id', bailAgentId)
        .gte('appointment_date', startOfDay.toISOString())
        .lte('appointment_date', endOfDay.toISOString());

      const allSlots = [];

      const now = new Date();
      const minFutureTime =
        new Date(now.getTime() + BUFFER_MINUTES * 60000);

      const startTotalMinutes =
        startHour * 60 + startMinute;

      const endTotalMinutes =
        endHour * 60 + endMinute;

      for (
        let totalMinutes = startTotalMinutes;
        totalMinutes < endTotalMinutes;
        totalMinutes += SLOT_DURATION_MINUTES
      ) {

        const slotHour = Math.floor(totalMinutes / 60);
        const slotMinute = totalMinutes % 60;

        const slotTime = new Date(selectedDate);
        slotTime.setHours(slotHour, slotMinute, 0, 0);

        if (slotTime <= minFutureTime) continue;

        const isBooked = bookedAppointments?.some(appt => {
          const apptTime = new Date(appt.appointment_date);
          const diff =
            Math.abs(slotTime - apptTime) / 60000;
          return diff < SLOT_DURATION_MINUTES;
        });

        if (!isBooked) {
          allSlots.push({
            time: slotTime.toISOString(),
            timeString:
              slotTime.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              }),
            available: true
          });
        }
      }

      return allSlots;

    } catch (error) {
      console.error('Error getting available time slots:', error);
      return [];
    }
  }

};  // <-- THIS PROPERLY CLOSES appointmentService


/* ======================================================
   AGENT AVAILABILITY SERVICE
====================================================== */

export const agentAvailabilityService = {

  async getAllAgents() {
    const { data } = await supabase
      .from('bail_agents')
      .select('*')
      .order('full_name');

    return data;
  },

  async getAgentWeeklySchedule(bailAgentId) {
    const { data } = await supabase
      .from('agent_weekly_schedules')
      .select('*')
      .eq('bail_agent_id', bailAgentId)
      .order('day_of_week');

    return data;
  }

};